import React from 'react';
import { useQuery } from '@apollo/client';
import { Select, Tag } from 'antd';
// Api
import { GET_CAMPAIGN_AGREEMENT_SIGNATORY_CANDIDATES } from 'api/campaignV2/queries';
// Types
import {
  GetCampaignAgreementSignatoryCandidates,
  GetCampaignAgreementSignatoryCandidatesVariables,
  GetCampaignAgreementSignatoryCandidates_getCampaignAgreementSignatoryCandidates as SignatoryCandidate,
} from 'api/campaignV2/types/GetCampaignAgreementSignatoryCandidates';
// Styles
import styles from './AgreementSignatorySelect.module.scss';

type AgreementSignatorySelectProps = {
  campaignId: string;
  /** The campaign owner, tagged in the list */
  ownerId?: string | null;
  value?: string | null;
  onChange: (signatoryId: string) => void;
  disabled?: boolean;
};

/** Help text for the signatory field, wherever it is chosen */
export const SIGNATORY_TIP =
  'The brand user who signs talent agreements for the brand. Set automatically when a brand user launches the campaign. Only the owner and team members who can launch it are listed.';

export const getSignatoryName = (
  user: Pick<SignatoryCandidate, 'firstName' | 'lastName' | 'email'>
): string =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;

const OwnerTag = (): JSX.Element => (
  <Tag color="blue" className={styles.tag}>
    Owner
  </Tag>
);

/**
 * Searchable list of the brand users who can sign the campaign's digital agreements:
 * the owner and the team members on the campaign who could launch it themselves.
 */
const AgreementSignatorySelect = ({
  campaignId,
  ownerId,
  value,
  onChange,
  disabled = false,
}: AgreementSignatorySelectProps): JSX.Element => {
  const { data, loading } = useQuery<
    GetCampaignAgreementSignatoryCandidates,
    GetCampaignAgreementSignatoryCandidatesVariables
  >(GET_CAMPAIGN_AGREEMENT_SIGNATORY_CANDIDATES, {
    variables: { input: { campaignId } },
    skip: !campaignId,
    fetchPolicy: 'cache-and-network',
  });

  const candidates = data?.getCampaignAgreementSignatoryCandidates ?? [];

  return (
    <Select
      showSearch
      optionLabelProp="label"
      className={styles.select}
      placeholder="Search by name or email"
      value={value || undefined}
      onChange={(signatoryId: string) => onChange(signatoryId)}
      loading={loading}
      disabled={disabled}
      notFoundContent={loading ? 'Loading…' : 'No eligible signatories'}
      filterOption={(input, option) =>
        String(option?.search ?? '').includes(input.trim().toLowerCase())
      }
    >
      {candidates.map((candidate) => (
        <Select.Option
          key={candidate.id}
          value={candidate.id}
          search={`${getSignatoryName(candidate)} ${
            candidate.email
          }`.toLowerCase()}
          label={
            <span className={styles.option}>
              <span className={styles.name}>{getSignatoryName(candidate)}</span>
              {candidate.id === ownerId && <OwnerTag />}
            </span>
          }
        >
          <span className={styles.option}>
            <span className={styles.name}>{getSignatoryName(candidate)}</span>
            <span className={styles.email}>{candidate.email}</span>
            {candidate.id === ownerId && <OwnerTag />}
          </span>
        </Select.Option>
      ))}
    </Select>
  );
};

export default AgreementSignatorySelect;
